import { type OperationVariables, type QueryResult } from '@apollo/client'
import { gql } from '__generated__'
import {
    CandidateFilterCategory,
    GetRostersByNameQuery,
    type SearchFacetInput,
    type SortField,
} from '__generated__/graphql'
import {
    createContext,
    useMemo,
    useState,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
} from 'react'
import { useQueryParams } from 'recruiter/hooks'
import { CategorizedSearchFacetInputs } from 'recruiter/pages/Research/Filters'
import { RosterCandidateTableViewId } from 'recruiter/pages/Rosters/RosterProfile/Search/RosterSearchProfile'

export const GetCandidateSearchSqlAllIds = gql(/* GraphQL */ `
    query GetCandidateSearchSqlAllIds(
        $rosterId: ID
        $filters: CandidateSearchFilters
    ) {
        candidateSearchSqlAllIds(rosterId: $rosterId, filters: $filters)
    }
`)

export const GetCandidatesForRosterSql = gql(/* GraphQL */ `
    query GetCandidatesForRosterSql(
        $rosterId: ID
        $enrichmentRosterId: ID
        $filters: CandidateSearchFilters
        $page: Int!
        $limit: Int!
        $sort: [CandidateSearchSortInput!]
        $referenceRosterIds: [ID!]
    ) {
        candidateSearchSql(
            rosterId: $rosterId
            enrichmentRosterId: $enrichmentRosterId
            filters: $filters
            page: $page
            limit: $limit
            sort: $sort
            referenceRosterIds: $referenceRosterIds
        ) {
            pageInfo {
                itemTotal
                pageNumber
                pageTotal
            }
            data {
                id
                candidateId
                rosterId
                rosterCandidateId
                rosterCandidateCreatedAt
                rosterCandidateSourceStatus
                rosterCandidateFavoriteCount
                profileImageKey
                internalProfileImageKey
                firstName
                lastName
                nickname
                email
                phone
                gender
                ethnicity
                starRatingAvg
                systemStatus
                recruitingStatus
                outreachStatus
                linkedinUrl
                visaStatus
                starRatingReference
                starRatingMostRecentScreen
                preferredEmail
                currentLocation
                previousCompany
                currentCompany
                currentCompanyId
                startDate
                previousStartDate
                previousTitle
                currentTitle
                startDates
                currentIndustry
                currentSector
                currentSectorDescription
                previousIndustry
                previousSector
                candidateDocumentId
                candidateDocumentKey
                resumeUpdatedDate
                currentWorkEmail
                mbaEducation
                mbaYear
                undergradYear
                undergradEducation
                undergradGpa
                undergradCollege
                graduateCollege
                act
                sat
                gmat
                ccatRawScore
                ccatPercentile
                publicBio
                preAdmitAttribute
                referenceAttribute
                cpiAttribute
                mbaTiering
                recruitingIndustryPref1
                recruitingIndustryPref2
                recruitingIndustryPref3
                recruitingSectorPref1
                recruitingSectorPref2
                recruitingSectorPref3
                recruitingGeoLocationPref1
                recruitingGeoLocationPref2
                recruitingGeoLocationPref3
                rosterCount
                hometown
                analystClassYear
                processStatus
                processStatusSummaryGroup
                rosterInterestFromBlast
                rosterNotInterestedReasonFromBlast
                rosterCommentsFromBlast
                rosterLocationFromBlast
                rosterSectorFromBlast
                rosterCandidate {
                    id
                    candidateProcessStatus {
                        id
                        updatedAt
                        processStatus {
                            id
                            status
                            statusLabel
                            processStatusSummaryGroup {
                                id
                                summaryGroup
                            }
                        }
                    }
                    sourceStatus
                    candidateColors {
                        id
                        rosterColor {
                            id
                            hexCode
                            order
                            updatedAt
                            createdAt
                            value
                        }
                    }
                    mostRecentJobBoardResponse {
                        id
                        officePrefs {
                            companyOffice {
                                officeLocation {
                                    value
                                }
                            }
                        }
                        sectorPrefs {
                            sector {
                                value
                            }
                        }
                    }
                    internalRecruiterNote {
                        id
                        note
                    }
                }
                referenceRosterCandidates {
                    rosterId
                    processStatus
                    processStatusLabel
                    summaryGroup
                    summaryGroupLabel
                }
            }
        }
    }
`)

interface CandidateSearchContextValueBase<T, R extends OperationVariables> {
    /** The list of currently active filters. */
    activeFilters: SearchFacetInput[]
    /** Replaces the active filters affecting the current search query. */
    setActiveFilters: (filters: SearchFacetInput[]) => void
    /** The list of currently active filters grouped by their categories. */
    activeFiltersByCategory: Partial<CategorizedSearchFacetInputs>
    /** The list of currently active sort fields. */
    activeSortFields: SortField[]
    /** The current query results. */
    data: QueryResult<T, R>['data']
    /** Retrieves additional data, appending it to the existing result set. */
    fetchMore: QueryResult<T, R>['fetchMore']
    /** Whether the query is currently loading data. */
    isLoading: QueryResult<T, R>['loading']
    /** Retrieves fresh data with updated query options. */
    refetch: QueryResult<T, R>['refetch']
    /** Applies a new set of sort fields that should be used to order the query results. */
    setActiveSortFields: Dispatch<SetStateAction<SortField[]>>
    /** The type of query being performed. This value can be used to type-narrow the result set of the `useCandidateSearch` hook. */
    type: 'standard' | 'roster'
    /** Whether the query should be overridden to retrieve all candidate records. Only applied to "roster" queries. */
    allCandidates: boolean | undefined
    /** The ID of the roster being searched. Only applied to "roster" queries. */
    onThisRosterId: string
    /** all candidate ids currently selected in the table */
    selectedCandidateIds: string[]
    setSelectedCandidateIds: (selectedCandidates: string[]) => void
    referenceRosters: GetRostersByNameQuery['rostersByName']['data'][number][]
    setReferenceRosters: Dispatch<
        SetStateAction<GetRostersByNameQuery['rostersByName']['data'][number][]>
    >
}

interface StandardCandidateSearchContextValue
    extends CandidateSearchContextValueBase<any, any> {
    type: 'standard'
}

interface RosterCandidateSearchContextValue
    extends CandidateSearchContextValueBase<any, any> {
    type: 'roster'
    allCandidates: boolean
    onThisRosterId: string
    referenceRosters: GetRostersByNameQuery['rostersByName']['data'][number][]
    setReferenceRosters: Dispatch<
        SetStateAction<GetRostersByNameQuery['rostersByName']['data'][number][]>
    >
}

type CandidateSearchContextValue =
    | StandardCandidateSearchContextValue
    | RosterCandidateSearchContextValue

export const CandidateSearchContext =
    createContext<CandidateSearchContextValue>(
        {} as CandidateSearchContextValue
    )

interface ProviderProps {
    /** The ID of the roster to retrieve candidate data for. If not provided, a standard candidate search will be performed. */
    rosterId?: string
    /** Whether the query should be overridden to retrieve all candidate records. Only applied to "roster" queries. */
    currentTableView: RosterCandidateTableViewId
    /** Any app content that should have access to this context's value. */
    children?: ReactNode
}

/**
 * Stores and provides access to data and actions related to candidate search
 * functionality.
 */
export const CandidateSearchProvider = ({
    rosterId = '',
    currentTableView,
    children,
}: ProviderProps) => {
    const { searchQueryParams, handleSetSearchQueryParams } = useQueryParams()
    const [candidateData] =
        useState<StandardCandidateSearchContextValue['data']>()
    const [rosterCandidateData] =
        useState<RosterCandidateSearchContextValue['data']>()

    const allCandidates = useMemo(
        () => currentTableView === 'all',
        [currentTableView]
    )

    // Strip the category metadata we add to active filters, since it isn't
    // valid as part of the query.
    const activeFilters: SearchFacetInput[] = useMemo(
        () => searchQueryParams,
        [searchQueryParams]
    )

    const [activeSortFields, setActiveSortFields] = useState<
        CandidateSearchContextValue['activeSortFields']
    >([])

    const [referenceRosters, setReferenceRosters] = useState<
        GetRostersByNameQuery['rostersByName']['data'][number][]
    >([])

    const [selectedCandidates, setSelectedCandidates] = useState<string[]>([])

    // All candidate search now uses SQL via CandidateSqlSearchProvider
    // This provider is only used for roster views, so no query needed here
    const refetchStandard = () => Promise.resolve({} as any)
    const isLoadingStandard = false
    const fetchMoreStandard = () => Promise.resolve({} as any)

    const refetchRoster = () => Promise.resolve({} as any)
    const isLoadingRoster = false
    const fetchMoreRoster = () => Promise.resolve({} as any)

    // Track the number of active filters in each category for display purposes.
    const activeFiltersByCategory = useMemo(() => {
        return Object.groupBy(
            searchQueryParams,
            ({ category }) => category ?? CandidateFilterCategory.All
        )
    }, [searchQueryParams])

    // Proxy the query params context's setter function so it can be used
    // conveniently alongside the `activeFilters` state.
    const setActiveFilters: CandidateSearchContextValue['setActiveFilters'] = (
        filters
    ) => {
        handleSetSearchQueryParams(filters)
    }

    // All candidate search now uses SQL via CandidateSqlSearchProvider
    // This provider is only used for roster views, so no refetch needed here

    if (rosterId === '') {
        const contextValue: StandardCandidateSearchContextValue = {
            type: 'standard',
            allCandidates: undefined,
            activeFilters,
            activeFiltersByCategory,
            activeSortFields,
            data: candidateData,
            onThisRosterId: '',
            fetchMore: fetchMoreStandard,
            isLoading: isLoadingStandard,
            refetch: refetchStandard,
            setActiveFilters,
            setActiveSortFields,
            selectedCandidateIds: selectedCandidates,
            setSelectedCandidateIds: setSelectedCandidates,
            referenceRosters: referenceRosters,
            setReferenceRosters: setReferenceRosters,
        }

        return (
            <CandidateSearchContext.Provider value={contextValue}>
                {children}
            </CandidateSearchContext.Provider>
        )
    } else {
        const contextValue: RosterCandidateSearchContextValue = {
            type: 'roster',
            allCandidates,
            activeFilters,
            activeFiltersByCategory,
            activeSortFields,
            onThisRosterId: !allCandidates ? rosterId : '',
            data: rosterCandidateData,
            fetchMore: fetchMoreRoster,
            isLoading: isLoadingRoster,
            refetch: refetchRoster,
            setActiveFilters,
            setActiveSortFields,
            selectedCandidateIds: selectedCandidates,
            setSelectedCandidateIds: setSelectedCandidates,
            referenceRosters: referenceRosters,
            setReferenceRosters: setReferenceRosters,
        }

        return (
            <CandidateSearchContext.Provider value={contextValue}>
                {children}
            </CandidateSearchContext.Provider>
        )
    }
}
